import React, { useContext, useState } from "react";
import { NewtworkContext } from "../../Layouts/Mint";
import { useWizard } from "react-use-wizard";
import { useAccount } from "wagmi";
import { validateCodeWord } from "../../../utils/api/codeWord";
import { useUserContext } from "../../../contexts/User";
import { Button, Link } from "../../Actions";
import { StepContent } from "./Components";
import { InputWithLabel } from "../../Inputs/InputWithLabel";
import { StatusBanner } from "../../StatusBanner/StatusBanner";
import { ConnectWalletSection } from "../../ConnectWalletSection/ConnectWalletSection";
import { alreadyMinted } from "../../../data/alreadyMinted";
import NFTPill from "../../NFTPill/NFTPill";

type BannerStates = "error" | "incorrectCodeWords" | "";

const CodeWord: React.FC = () => {
  const { address } = useAccount();
  const network = useContext(NewtworkContext);
  const { user } = useUserContext();
  const { nextStep, handleStep, goToStep } = useWizard();
  const [codewordStatus, setCodewordStatus] = useState<{
    state: BannerStates;
    message?: string;
  }>({ state: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeWords, setCodeWords] = useState<Array<string | undefined>>([
    undefined,
    undefined,
    undefined
  ]);

  const [submissionAttempts, setSubmissionAttempts] = useState(0);

  // If any of the codewords are undefined they can not enter the next step
  const isButtonDisabled =
    codeWords.some(el => el === undefined) ||
    submissionAttempts > 3 ||
    isSubmitting ||
    user?.hasMinted;

  handleStep(() => {
    network?.setActiveStep(2);
  });

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    if (!address) return;

    const codeword = codeWords.join("-").toLowerCase();
    try {
      setIsSubmitting(true);

      const { data, error } = await validateCodeWord({
        walletAddress: address,
        codeword
      });

      if (!error) {
        nextStep();
      }
      if (error) {
        setSubmissionAttempts(attempts => attempts + 1);
        setCodewordStatus({ state: "incorrectCodeWords", message: error });
      }
    } catch {
      setCodewordStatus({
        state: "error",
        message: "Sorry something went wrong"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  type TBannerOptions = {
    [key: string]: { title: string; message: React.ReactNode };
  };

  const bannerMessages: TBannerOptions = {
    error: {
      title: "Something went wrong",
      message: <>Its not you, it&apos;s us. Please try again.</>
    },
    incorrectCodeWords: {
      title: "Thats not right",
      message: (
        <>
          Make sure each word is spelled correctly and doesn&apos;t include any
          spaces or dashes.{" "}
          <b className="text-coinage-black">
            {4 - submissionAttempts} tries left
          </b>
          .
        </>
      )
    }
  };

  const handleChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
    key: number
  ) => {
    const val = evt.target.value;
    const newCodewords = [...codeWords];
    newCodewords[key] = val;
    setCodeWords(newCodewords);
  };

  const title = (
    <>
      <span className="font-normal">What is your</span>
      <span className="text-coinage-orange mb-2"> code phrase</span>
      <span className="font-normal">?</span>
    </>
  );
  if (!address)
    return (
      <div>
        <h3 className="subhead1">{title}</h3>
        <ConnectWalletSection />
      </div>
    );

  return (
    <StepContent description="Please enter your phrase." title={title}>
      <form
        className="flex gap-2 flex-col items-center"
        onSubmit={handleSubmit}>
        <InputWithLabel
          label="Word 1"
          onChange={evt => handleChange(evt, 0)}
          value={codeWords[0]}
        />
        <InputWithLabel
          label="Word 2"
          onChange={evt => handleChange(evt, 1)}
          value={codeWords[1]}
        />
        <InputWithLabel
          label="Word 3"
          onChange={evt => handleChange(evt, 2)}
          value={codeWords[2]}
        />

        <Button
          type="submit"
          className="mt-4"
          modifier="primary"
          analyticsProperty="Submit Codewords"
          disabled={isButtonDisabled}>
          Enter
        </Button>
      </form>

      {user?.hasMinted ? (
        <StatusBanner title={alreadyMinted.title}>
          {alreadyMinted.description}
        </StatusBanner>
      ) : (
        <div className="flex max-w-4xl p-8 flex-col items-center mx-auto gap-2 border-t-[1px] border-coinage-stroke">
          <h3 className="subhead1 mb-0">Don&apos;t have a code phrase?</h3>
          <p className="body1 mt-0 mb-6">
            Apply for a Network Membership Pass to get one.
          </p>

          <Link
            analyticsProperty="coinage premint"
            modifier="primary"
            href="https://www.premint.xyz/coinage-network-nft">
            Register with PREMINT
          </Link>
        </div>
      )}
      {["error", "incorrectCodeWords"].includes(codewordStatus.state) && (
        <StatusBanner
          variant="error"
          title={bannerMessages[codewordStatus.state].title}>
          {bannerMessages[codewordStatus.state].message}
        </StatusBanner>
      )}
    </StepContent>
  );
};

export default CodeWord;
